import React, { useEffect, useState } from 'react';
import './App.css';
import AuthForm from './components/AuthForm';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import Landing from './components/Landing';
import Terms from './components/Terms';
import useDocumentStore from './stores/documentStore';
import useAuthStore from './stores/authStore';
import 'antd/dist/reset.css';
import Header from './components/Header';
import DocumentNotFound from './components/DocumentNotFound';
import useUserSettingsStore from './stores/userSettingsStore';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

function App() {
  // Получаем состояние и действия из хранилищ
  const { 
    documents, 
    activeDocumentId, 
    documentResult, 
    loading, 
    error,
    isFetchingDocumentById,
    fetchDocuments, 
    deleteDocument, 
    formatDate,
    pollDocumentStatus,
    fetchDocumentById,
    fetchProcessedDocumentsCount
  } = useDocumentStore();
  
  const { isAuthenticated, checkAuth, logout } = useAuthStore();

  const { isOpen: showSettings } = useUserSettingsStore();

  // Получаем функцию fetchSettings из хранилища настроек
  const { fetchSettings } = useUserSettingsStore();

  // Состояние для модального окна подтверждения удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentIdToDelete, setDocumentIdToDelete] = useState(null);

  // Эффект для инициализации приложения
  useEffect(() => {
    const path = window.location.pathname;
    const isAuth = checkAuth();
    const match = path.match(/^\/documents\/([^/]+)$/);

    // Если путь - страница документа и пользователь не авторизован,
    // не делаем редирект, пока не проверим публичный доступ
    if (!isAuth && match) {
      return;
    }

    // Проверяем авторизацию и делаем редиректы только для защищенных маршрутов
    if (!isAuth && path.startsWith('/documents')) {
      window.location.href = '/auth';
      return;
    }

    // Если есть токен и пользователь на странице авторизации - редиректим на /documents
    if (isAuth && path === '/auth') {
      window.location.href = '/documents';
      return;
    }

    // Загружаем документы только если:
    // 1. Есть токен
    // 2. Путь начинается с /documents
    if (isAuth && path.startsWith('/documents')) {
      fetchDocuments();
      fetchSettings(); // Добавляем загрузку настроек
      fetchProcessedDocumentsCount(); // Загружаем количество обработанных документов
      const interval = setInterval(fetchDocuments, 5000);
      return () => clearInterval(interval);
    }
  }, [checkAuth, fetchDocuments, fetchSettings, fetchProcessedDocumentsCount]);

  // Эффект для обработки URL с ID документа
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/documents\/([^/]+)$/);
    
    if (match && isAuthenticated) {
      const documentId = match[1];
      if (!activeDocumentId || activeDocumentId !== documentId) {
        useDocumentStore.getState().handleDocumentClick(documentId);
      }
    }
  }, [isAuthenticated, activeDocumentId, useDocumentStore.getState().handleDocumentClick]);

  // Эффект для опроса статуса документа
  useEffect(() => {
    let interval;
    
    if (activeDocumentId && loading) {
      interval = setInterval(() => {
        pollDocumentStatus(activeDocumentId).then(shouldContinue => {
          if (!shouldContinue && interval) {
            clearInterval(interval);
          }
        });
      }, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [activeDocumentId, loading, pollDocumentStatus]);

  // Добавляем эффект для проверки публичного доступа
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/documents\/([^/]+)$/);
    
    if (match && isFetchingDocumentById == null) {
      const documentId = match[1];
      fetchDocumentById(documentId);
    }
  }, [isAuthenticated, documentResult, activeDocumentId, fetchDocumentById]);

  const handleFileChange = (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      useDocumentStore.getState().uploadDocuments(selectedFiles);
      useUserSettingsStore.getState().closeSettings();
    }
  };

  const handleDelete = (documentId, event) => {
    event.stopPropagation();
    setDocumentIdToDelete(documentId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (documentIdToDelete) {
      deleteDocument(documentIdToDelete);
    }
    setIsDeleteModalOpen(false);
    setDocumentIdToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDocumentIdToDelete(null);
  };

  const handleSettingsClick = () => {
    useUserSettingsStore.getState().openSettings();
  };

  const handleCloseSettings = () => {
    useUserSettingsStore.getState().closeSettings();
  };

  const handleLogout = () => {
    useUserSettingsStore.getState().closeSettings();
    logout();
  };

  const handleDocumentClick = (documentId) => {
    useUserSettingsStore.getState().closeSettings();
    useDocumentStore.getState().handleDocumentClick(documentId);
  };

  const renderPublicView = () => (
    <div className="container public-view">
      <Header />
      <MainContent 
        error={error}
        activeDocumentId={activeDocumentId}
        documentResult={documentResult}
        handleDocumentClick={handleDocumentClick}
        isPublicView={true}
        showSettings={showSettings}
        onCloseSettings={handleCloseSettings}
      />
    </div>
  );

  const renderMainPage = () => (
    <div className="container">
      <Sidebar 
        documents={documents}
        activeDocumentId={activeDocumentId}
        handleDocumentClick={handleDocumentClick}
        handleDelete={handleDelete}
        handleFileChange={handleFileChange}
        handleLogout={handleLogout}
        formatDate={formatDate}
        handleSettingsClick={handleSettingsClick}
      />
      <MainContent 
        error={error}
        activeDocumentId={activeDocumentId}
        documentResult={documentResult}
        handleDocumentClick={handleDocumentClick}
        isPublicView={false}
        showSettings={showSettings}
        onCloseSettings={handleCloseSettings}
      />
    </div>
  );

  const renderPage = () => {
    const path = window.location.pathname;
    
    if (path === '/') {
      return (
        <div className="App-landing">
          <Landing />
        </div>
      );
    }
    
    if (path === '/auth') {
      return (
        <div className="authform-page">
          <AuthForm />
        </div>
      );
    }

    if (path === '/terms') {
      return (
        <div className="App-landing">
          <Terms />
        </div>
      );
    }

    // Проверяем путь на соответствие /documents/{id}
    const match = path.match(/^\/documents\/([^/]+)$/);
    if (match) {

      // Пока происходит загрузка документа, ничего не рендерим
      if (isFetchingDocumentById !== false) {
        return null;
      }
      
      // Если авторизован
      if (isAuthenticated) {
        // Если в ссылке есть ID документа, но результат по нему не получен (404)
        if (!documentResult) {
          return <DocumentNotFound />;
        }
        // Если получили чужой документ
        if (!documentResult.is_owned) {
          return renderPublicView();
        }
        // Есть результат и документ принадлежит пользователю
        return renderMainPage();
      }
      
      // Неавторизованный пользователь получил документ
      if (documentResult) {
        return renderPublicView();
      }

      // Если в ссылке есть ID документа, но результат по нему не получен (404)
      return <DocumentNotFound />;
    }

    // Для всех остальных путей /documents/* требуем авторизацию
    if (path.startsWith('/documents')) {
      if (!isAuthenticated) {
        window.location.href = '/auth';
        return null;
      }
      return renderMainPage();
    }

    // Если путь неизвестен - редирект на главную
    window.location.href = '/';
    return null;
  };

  return (
    <div className={`App ${!isAuthenticated ? 'public' : ''}`}>
      {renderPage()}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

export default App;
